import React from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { METAVERSE_FOLDER } from "../../utils/media-utils";
import configs from "../../utils/configs";
import { processThumbnail } from "../../utils/thumbnails";
import { getSignedUrl } from "../../utils/artwork-utils";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  ProgressBar,
  tokens
} from "@fluentui/react-components";
import { ArrowUploadFilled } from "@fluentui/react-icons";
import { useUploadFormContext } from "../contexts/UploadFormContext";
import { useWorkspaceContext } from "../contexts/WorkspaceContext";
import configManager from "../../singleton/ConfigManager";
import useAssetUploadCapacity from "../hooks/useAssetUploadCapacity";
import { getAssetUploadCapacity } from "../../utils/runtime-settings";
import { FormattedMessage } from "react-intl";
import { getToken } from "../../utils/getToken";

const useStyles = makeStyles({
  dropzoneCard: {
    display: "flex",
    width: "100%",
    "@media (min-width: 576px)": {
      width: "400px",
      margin: "auto"
    },
    height: "300px",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "var(--card-corner-radius, 4px)",
    border: "1px dashed var(--primary)",
    background: "var(--primary-color-background)",
    boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.14), 0px 0px 2px 0px rgba(0, 0, 0, 0.12)",
    "&:hover": {
      border: "1px solid var(--primary)",
      cursor: "pointer"
    }
  },
  dropzoneContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    height: "100%",
    width: "100%"
  },
  dropzoneContentUpload: {
    display: "flex",
    maxWidth: "227px",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    height: "100%",
    width: "100%"
  },
  firstText: {
    color: tokens.colorNeutralForeground1,
    textAlign: "center",
    fontSize: "var(--Font-size-300, 14px)",
    fontWeight: 600
  },
  secondText: {
    color: tokens.colorNeutralForeground2,
    fontFamily: "var(--Font-family-Base, 'Segoe UI')",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal"
  },
  uploadIcon: {
    width: "40px",
    minWidth: "40px",
    height: "40px",
    "& path": {
      fill: "var(--colorBrandBackground)",
      color: "var(--colorBrandBackground)"
    }
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  }
});

function NewDropzone({ onFileLoaded }, ref) {
  const styles = useStyles();
  const { indefiniteLoading, setIndefiniteLoading, errorUpload, setErrorUpload } = useUploadFormContext();
  const { selectedWorkspace } = useWorkspaceContext();

  const [progressLoading, setProgressLoading] = React.useState(0);
  const [assetUploadCapacityExceeded, setAssetUploadCapacityExceeded] = React.useState(false);
  const [currentFileSize, setCurrentFileSize] = React.useState(0);

  const maxFileSize = useAssetUploadCapacity();

  const processFiles = async acceptedFiles => {
    try {
      setErrorUpload(false);
      const acceptedFile = acceptedFiles[0];

      if (!acceptedFile) {
        throw new Error("The file is not valid or no file was selected.");
      }

      // List of accepted file extensions
      const acceptedExtensions = ["glb", "png", "jpg", "jpeg", "gif", "pdf", "mp4", "mp3"];

      // Get the file extension
      const fileExtension = acceptedFile.name.split(".").pop().toLowerCase();

      // Check if the file extension is in the list of accepted extensions
      if (!acceptedExtensions.includes(fileExtension)) {
        alert("File type not supported. Only GLB, PNG, JPG, GIF, PDF, MP4 and MP3 files are allowed.");
        return;
      }

      const fileSizeMB = (acceptedFile.size / (1024 * 1024)).toFixed(2);
      setCurrentFileSize(fileSizeMB);
      if (acceptedFile.size > 1024 * 1024 * getAssetUploadCapacity(configManager.config)) {
        setAssetUploadCapacityExceeded(true);
        return;
      }

      setIndefiniteLoading(true);

      const blob = acceptedFile.slice(0, acceptedFile.size, acceptedFile.type);
      const newFile = new File([blob], acceptedFile.name, { type: acceptedFile.type });

      let localThumbnailBlob = null;
      try {
        if (!acceptedFile.type.includes("audio/")) {
          localThumbnailBlob = await processThumbnail(
            newFile,
            acceptedFile.name,
            { returnBlob: true, mimeType: acceptedFile.type }
          );
        }
      } catch (error) {
        console.error("Error generating preview thumbnail", error);
      }

      setIndefiniteLoading(false);
      onFileLoaded(newFile, localThumbnailBlob);
    } catch (e) {
      console.error("Error processing file", e);
      setErrorUpload(true);
      setIndefiniteLoading(false);
      setProgressLoading(0);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: processFiles,
    accept: {
      "model/gltf-binary": [".glb"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/gif": [".gif"],
      "application/pdf": [".pdf"],
      "video/mp4": [".mp4"],
      "audio/mpeg": [".mp3"]
    }
  });

  // Expose the processFiles function through ref
  React.useImperativeHandle(ref, () => ({
    processFiles
  }));

  return (
    <>
      <div className={styles.dropzoneCard} {...(!progressLoading && !indefiniteLoading ? getRootProps() : {})}>
        <input {...getInputProps()} style={{ display: "none" }} type="file" />
        <div className={styles.dropzoneContent}>
          {!progressLoading && !indefiniteLoading && (
            <div className={styles.dropzoneContentUpload}>
              <ArrowUploadFilled className={styles.uploadIcon} />
              <p className={styles.firstText}>
                <FormattedMessage
                  id="upload-object-modal.supported-files"
                  defaultMessage="Only GLB, PNG, JPG, GIF, PDF, MP4 and MP3 files"
                />
              </p>
              <p className={styles.secondText}>
                <FormattedMessage
                  id="upload-object-modal.max-file-size"
                  defaultMessage="Max file size: {maxFileSize} MB"
                  values={{ maxFileSize }}
                />
              </p>
            </div>
          )}
          {progressLoading && !indefiniteLoading ? <ProgressBar value={progressLoading}></ProgressBar> : ""}
          {indefiniteLoading && !progressLoading ? <ProgressBar></ProgressBar> : ""}
          {errorUpload && (
            <p style={{ color: "red", marginBottom: "10px" }}>
              <FormattedMessage
                id="upload-object-modal.error-message"
                defaultMessage="Error uploading file. Please try again."
              />
            </p>
          )}
        </div>
      </div>
      <Dialog
        open={assetUploadCapacityExceeded}
        onOpenChange={(e, data) => {
          setAssetUploadCapacityExceeded(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              <FormattedMessage id="upload-object-modal.file-size-error" defaultMessage="File size limit exceeded" />
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <FormattedMessage
                id="upload-object-modal.file-size-error-description"
                defaultMessage="The file you are trying to upload is larger than the upload capacity allowed for this Verse."
              />
              <span style={{ color: "var(--Red, #EB5757)" }}>
                <FormattedMessage
                  id="upload-object-modal.file-size-error-current"
                  defaultMessage="Current file size: {currentFileSize} MB"
                  values={{ currentFileSize }}
                />
              </span>
              <span style={{ color: "var(--Red, #EB5757)" }}>
                <FormattedMessage
                  id="upload-object-modal.file-size-error-limit"
                  defaultMessage="Maximum allowed file size: {maxFileSize} MB"
                  values={{ maxFileSize }}
                />
              </span>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAssetUploadCapacityExceeded(false)}>
                <FormattedMessage id="upload-object-modal.ok" defaultMessage="Ok" />
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

NewDropzone.propTypes = {
  onFileLoaded: PropTypes.func.isRequired
};

export default React.forwardRef(NewDropzone);
